import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Sparkles, Calendar as CalendarIcon } from 'lucide-react';

export const MedicalSpaPortfolio: React.FC = () => {
  const technologies = [
    'React', 'Node.js', 'MySQL', 'Stripe API', 'AJAX', 'AWS'
  ];

  const features = [
    'Online Booking System',
    'Treatment Packages',
    'Membership Programs',
    'Before/After Gallery',
    'Consultation Scheduling',
    'Payment Processing',
    'Client Portal',
    'Staff Management'
  ];

  const results = [
    { metric: 'Online Bookings', value: '80%', description: 'Of appointments now booked online' },
    { metric: 'Revenue Increase', value: '60%', description: 'Growth in treatment bookings' },
    { metric: 'Client Retention', value: '75%', description: 'Improvement through membership program' },
    { metric: 'Booking Efficiency', value: '90%', description: 'Reduction in scheduling conflicts' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Medical Spa
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Radiance Medical Spa
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A sophisticated medical spa platform with advanced booking system, treatment packages, 
                and membership programs that increased online bookings by 80% and revenue by 60%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Medical Spa Interior"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Radiance Medical Spa needed a sophisticated online presence that could handle complex 
                  treatment scheduling, package deals, and membership programs while maintaining the 
                  luxury aesthetic their clients expect.
                </p>
                <p className="mb-6">
                  We developed a custom React-based platform with advanced booking capabilities, 
                  integrated payment processing, and a comprehensive client management system. 
                  The solution includes before/after galleries, treatment tracking, and automated 
                  follow-up systems.
                </p>
                <p>
                  The implementation resulted in 80% of appointments being booked online, 60% revenue 
                  increase, and 75% improvement in client retention through the membership program.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">10 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">November 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team Size:</span>
                    <span className="font-medium">4 specialists</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Luxury medical spa features designed to enhance client experience and streamline operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Outstanding improvements in client engagement and business growth metrics.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Sparkles className="h-8 w-8 text-purple-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Complex Scheduling</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Managing complex treatment schedules with varying durations, staff requirements, 
                and equipment availability while preventing double bookings.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed an intelligent scheduling system with real-time 
                availability checking, staff assignment automation, and conflict prevention algorithms.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <CalendarIcon className="h-8 w-8 text-pink-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Treatment Packages</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Creating flexible treatment packages and membership programs with varying pricing, 
                expiration dates, and usage tracking.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Built a comprehensive package management system with 
                automated tracking, expiration alerts, and flexible redemption options.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The new platform has elevated our entire client experience. Online bookings have increased 
            dramatically, and our membership program has significantly improved client retention."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Dr. Sarah Williams"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Dr. Sarah Williams</div>
              <div className="text-primary-200">Medical Director, Radiance Medical Spa</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Medical Spa?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a luxury platform that enhances client experience and grows your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};